/**
 * Constants for the Address Search Field component
 * Extracted to avoid magic numbers and improve maintainability
 */

/**
 * Validation constraints for address search input
 */
export const ADDRESS_SEARCH_VALIDATION = {
  /** Minimum number of characters required to trigger a search */
  MIN_QUERY_LENGTH: 3,
  /** Maximum number of characters allowed in search query */
  MAX_QUERY_LENGTH: 200,
} as const;

/**
 * API request timing configuration
 */
export const ADDRESS_SEARCH_TIMING = {
  /** Default debounce delay in milliseconds before triggering search */
  DEFAULT_DEBOUNCE_MS: 500,
  /** Adaptive debounce delay after first keystroke for better UX */
  ADAPTIVE_DEBOUNCE_MS: 250,
  /** Request timeout in milliseconds */
  REQUEST_TIMEOUT_MS: 10000,
} as const;

/**
 * Default values for address search configuration
 */
export const ADDRESS_SEARCH_DEFAULTS = {
  /** Default maximum number of results to display */
  MAX_RESULTS: 10,
  /** Default API endpoint for address search */
  API_ENDPOINT: "/api/places/autocomplete",
} as const;

/**
 * Keyboard navigation keys for accessibility
 */
export const KEYBOARD_KEYS = {
  ARROW_DOWN: "ArrowDown",
  ARROW_UP: "ArrowUp",
  ENTER: "Enter",
  ESCAPE: "Escape",
  HOME: "Home",
  END: "End",
} as const;

/**
 * Error messages for address search
 */
export const ADDRESS_SEARCH_ERRORS = {
  TIMEOUT: "Request timeout - address search took too long",
  API_FAILURE: "API request failed",
  PREDICTION_NOT_FOUND: "Prediction not found",
  NETWORK_ERROR: "Network error occurred",
  GENERIC: "Unable to search addresses",
} as const;

/**
 * Success/info messages
 */
export const ADDRESS_SEARCH_MESSAGES = {
  SEARCHING: "Searching for addresses...",
  NO_RESULTS: "No addresses found",
  ERROR_LOADING: "Error loading address results",
  RESULTS_FOUND: (count: number) =>
    `${count} ${count === 1 ? "address" : "addresses"} found`,
} as const;

/**
 * Mode types for the address search field
 */
export const ADDRESS_SEARCH_MODES = {
  SEARCH: "search",
  MANUAL: "manual",
  SELECTED: "selected",
} as const;

/**
 * Entry method tracking values
 */
export const ENTRY_METHODS = {
  MANUAL: "manual",
  AUTOMATIC: "automatic",
} as const;
